import type { FormSubmitPayload, FormSubmitResponse, MockSubmitOptions } from './form-widget.types';

const DEFAULT_DELAY_MS = 350;

export class FormSubmitError extends Error {
  response?: FormSubmitResponse;

  constructor(message: string, response?: FormSubmitResponse) {
    super(message);
    this.name = 'FormSubmitError';
    this.response = response;
  }
}

export function mockSubmitForm(
  payload: FormSubmitPayload,
  options: MockSubmitOptions = {},
): Promise<FormSubmitResponse> {
  void payload;
  const delayMs = options.delayMs ?? DEFAULT_DELAY_MS;
  const result = options.result ?? 'success';

  return new Promise((resolve, reject) => {
    window.setTimeout(() => {
      if (result === 'error') {
        reject(
          new FormSubmitError('Mock form submission failed', {
            success: false,
            message: 'No fue posible enviar el formulario',
          }),
        );
        return;
      }

      resolve({
        success: true,
        message: 'Formulario enviado correctamente',
      });
    }, delayMs);
  });
}

export function fallbackErrorMessage(status: number): string {
  if (status === 419) {
    return 'Tu sesión expiró. Recarga la página e intenta enviarlo nuevamente.';
  }
  if (status === 429) {
    return 'Estamos recibiendo muchas solicitudes en este momento. Espera unos segundos e intenta nuevamente.';
  }
  if (status >= 500) {
    return 'Tuvimos un problema temporal al procesar tu solicitud. Por favor intenta nuevamente en unos minutos.';
  }
  return 'No fue posible enviar el formulario. Revisa los datos ingresados e intenta nuevamente.';
}

const NETWORK_ERROR_MESSAGE =
  'No pudimos conectar con el servidor. Revisa tu conexión a internet e intenta nuevamente.';

const CLIENT_ERROR_REPORT_URL = 'https://widget.adipa.dev/api/client-error';
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

const REPORT_SESSION_ID = Math.random().toString(36).slice(2, 10);

export type SubmitFailureReport = {
  kind: 'network' | 'non-json' | 'server-error';
  status: number | null;
  bodySnippet: string | null;
  errorMessage: string | null;
};

export type SubmitFailureContext = Pick<FormSubmitPayload, 'source' | 'trackingId' | 'pageUrl'> & {
  variant: string;
  platformCountry?: string;
};

export function reportSubmitFailure(
  payload: SubmitFailureContext,
  submitUrl: string,
  report: SubmitFailureReport,
): void {
  try {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
      return;
    }
    if (typeof window !== 'undefined' && LOCAL_HOSTNAMES.includes(window.location.hostname)) {
      return;
    }
    void fetch(CLIENT_ERROR_REPORT_URL, {
      method: 'POST',
      credentials: 'omit',
      keepalive: true,
      headers: {
        'Content-Type': 'text/plain',
      },
      body: JSON.stringify({
        ...report,
        variant: payload.variant,
        source: payload.source,
        trackingId: payload.trackingId,
        platformCountry: payload.platformCountry,
        pageUrl: payload.pageUrl,
        submitUrl,
        sessionId: REPORT_SESSION_ID,
        visibility: typeof document !== 'undefined' ? document.visibilityState : null,
        occurredAt: new Date().toISOString(),
      }),
    }).catch(() => {});
  } catch {}
}

function hasFile(payload: FormSubmitPayload) {
  return Object.values(payload.fields).some(
    (value) => typeof File !== 'undefined' && value instanceof File,
  );
}

/** Con archivos el envío va como multipart: cada campo plano y el resto del envelope al lado. */
function toFormData(payload: FormSubmitPayload): FormData {
  const data = new FormData();
  const { fields, accountFields, widgetFields, ...envelope } = payload;

  Object.entries(envelope).forEach(([key, value]) => {
    if (value !== undefined) data.set(key, String(value));
  });
  Object.entries(fields).forEach(([key, value]) => {
    data.set(key, value instanceof File ? value : String(value));
  });
  if (accountFields) data.set('accountFields', JSON.stringify(accountFields));
  if (widgetFields) data.set('widgetFields', JSON.stringify(widgetFields));

  return data;
}

function createRequestInit(payload: FormSubmitPayload): RequestInit {
  if (hasFile(payload)) {
    return { method: 'POST', body: toFormData(payload) };
  }

  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  };
}

export async function submitForm(
  payload: FormSubmitPayload,
  options: MockSubmitOptions & { submitUrl?: string } = {},
): Promise<FormSubmitResponse> {
  if (!options.submitUrl) {
    return mockSubmitForm(payload, options);
  }

  let response: Response;

  try {
    response = await fetch(options.submitUrl, createRequestInit(payload));
  } catch (error) {
    console.error('[adipa-form-widget] network error submitting form', {
      variant: payload.variant,
      submitUrl: options.submitUrl,
      error,
    });
    reportSubmitFailure(payload, options.submitUrl, {
      kind: 'network',
      status: null,
      bodySnippet: null,
      errorMessage: String(error),
    });
    throw new FormSubmitError(NETWORK_ERROR_MESSAGE, {
      success: false,
      message: NETWORK_ERROR_MESSAGE,
    });
  }

  let rawBody = '';

  try {
    rawBody = await response.text();
  } catch {
    rawBody = '';
  }

  let body: FormSubmitResponse | null = null;

  try {
    body = JSON.parse(rawBody) as FormSubmitResponse;
  } catch {
    body = null;
  }

  if (body === null) {
    console.error('[adipa-form-widget] non-JSON response from form endpoint', {
      variant: payload.variant,
      status: response.status,
      bodySnippet: rawBody.slice(0, 300),
    });
    reportSubmitFailure(payload, options.submitUrl, {
      kind: 'non-json',
      status: typeof response.status === 'number' ? response.status : null,
      bodySnippet: rawBody.slice(0, 300) || null,
      errorMessage: null,
    });
  } else if (response.status >= 500) {
    reportSubmitFailure(payload, options.submitUrl, {
      kind: 'server-error',
      status: response.status,
      bodySnippet: rawBody.slice(0, 300) || null,
      errorMessage: null,
    });
  }

  const normalizedBody = body ?? {
    success: response.ok,
    message: response.ok
      ? 'Formulario enviado correctamente'
      : fallbackErrorMessage(response.status),
  };

  if (!response.ok || !normalizedBody.success) {
    console.error('[adipa-form-widget] form submission failed', {
      variant: payload.variant,
      status: response.status,
      response: normalizedBody,
    });
    throw new FormSubmitError(
      normalizedBody.message ?? fallbackErrorMessage(response.status),
      normalizedBody,
    );
  }

  return normalizedBody;
}
